import React, { useState } from 'react';
import { Button, Card, Col, Form, FormControl, InputGroup } from 'react-bootstrap';
import { loadStripe } from '@stripe/stripe-js';
import { CardElement, Elements, useElements, useStripe } from '@stripe/react-stripe-js';
import Axios from "axios";
import 'styles/payment.scss';

const TEST_PUBLIC_KEY = "pk_test_csSPytfLrBN95t56HcDCjEC600OOe8acWB";
const LIVE_PUBLIC_KEY = "pk_live_R6m6TSy0eCI1YzZUY7XsSEWv00C2NhMcbT";

// Make sure to call `loadStripe` outside of a component’s render to avoid
// recreating the `Stripe` object on every render.
const stripePromise = loadStripe(LIVE_PUBLIC_KEY);

const LATO_FONT = {
    fonts: [{
        cssSrc: "https://fonts.googleapis.com/css?family=Lato:300,400,700",
    }]
};

const CARD_OPTIONS = {
    style: {
        base: {
            iconColor: '#c4f0ff',
            color: '#acacac',
            fontWeight: 400,
            fontFamily: 'Lato',
            fontSize: `${parseFloat(getComputedStyle(document.documentElement).fontSize)}px`,
            ':-webkit-autofill': {
                color: '#acacac',
            },
            '::placeholder': {
                color: '#acacac',
            },
        },
    },
};

const CheckoutForm = () => {
    const stripe = useStripe();
    const elements = useElements();
    const [error, setError] = useState(null);
    const [cardComplete, setCardComplete] = useState(false);
    const [processing, setProcessing] = useState(false);
    const [showAreYouSure, setShowAreYouSure] = useState(false);
    const [processedSuccessfully, setProcessedSuccessfully] = useState(false);
    const [billingDetails, setBillingDetails] = useState({
        email: '',
        phone: '',
        name: '',
    });
    const [customerDetails, setCustomerDetails] = useState({
        description: "",
        firstName: "",
        lastName: "",
        phoneNumber: "",
        email: "",
        mailingAddress1: "",
        mailingAddress2: "",
        city: "",
        state: "",
        zip: "",
        billingName: "",
        amountInDollars: "",
    });

    const handleSubmit = (event) => {
        event.preventDefault();

        if (!stripe || !elements) {
            // Stripe.js has not loaded yet. Make sure to disable
            // form submission until Stripe.js has loaded.
            return;
        }

        if (error) {
            elements.getElement('card').focus();
            return;
        }

        if (!cardComplete) {
            return;
        }

        setShowAreYouSure(true);
    };

    const submitCharge = async () => {
        setProcessing(true);
        const payload = await stripe.createPaymentMethod({
            type: 'card',
            card: elements.getElement(CardElement),
            billing_details: billingDetails,
        });

        if (payload.error) {
            setError(payload.error);
            return;
        }

        Axios.post("/process_charge", {
            "description": customerDetails.description,
            "first_name": customerDetails.firstName,
            "last_name": customerDetails.lastName,
            "phone": customerDetails.phoneNumber,
            "mailing_address_1": customerDetails.mailingAddress1,
            "mailing_address_2": customerDetails.mailingAddress2,
            "city": customerDetails.city,
            "state": customerDetails.state,
            "zip": customerDetails.zip,
            "billing_name": customerDetails.billingName,
            "receipt_email": customerDetails.email,
            "payment_amount": Number(customerDetails.amountInDollars) * 100,
            "payment_method_id": payload.paymentMethod.id,
        })
        .then(r => r.data.success ? setProcessedSuccessfully(true) : setError(r.data.error))
        .catch(e => setError(e));
        setShowAreYouSure(false);
        setProcessing(false);
    }

    const form = (
        <Form onSubmit={handleSubmit}>
            <Form.Group>
                <Form.Label>Commission Description</Form.Label>
                <Form.Control
                    type="text"
                    name="productInfo"
                    placeholder="Payment for surgical illustration"
                    as="textarea"
                    rows="5"
                    required
                    isValid={customerDetails.description}
                    onChange={(e) => {
                        setCustomerDetails({ ...customerDetails, description: e.target.value });
                    }}
                />
            </Form.Group>
            <Form.Group>
                <br />
                <Form.Label>Amount to charge to your card (in USD)</Form.Label>
                <InputGroup className="mb-3">
                    <InputGroup.Prepend>
                        <InputGroup.Text>$</InputGroup.Text>
                    </InputGroup.Prepend>
                    <FormControl
                        name="amount"
                        aria-label="Amount"
                        isValid={Number.isFinite(customerDetails.amountInDollars)}
                        onChange={(e) => {
                            setCustomerDetails({ ...customerDetails, amountInDollars: Number(e.target.value) });
                        }}
                        required
                    />
                </InputGroup>
            </Form.Group>
            <br />
            <Form.Group as={Form.Row}>
                <Form.Label column sm={3}>First name</Form.Label>
                <Col sm={9}>
                    <Form.Control
                        name="firstName"
                        type="text"
                        placeholder="Jane"
                        isValid={customerDetails.firstName}
                        onChange={(e) => {
                            setCustomerDetails({ ...customerDetails, firstName: e.target.value });
                        }}
                    />
                </Col>
            </Form.Group>
            <Form.Group as={Form.Row}>
                <Form.Label column sm={3} required>Last name</Form.Label>
                <Col sm={9}>
                    <Form.Control
                        name="lastName"
                        type="text"
                        placeholder="Doe"
                        isValid={customerDetails.lastName}
                        onChange={(e) => {
                            setCustomerDetails({ ...customerDetails, lastName: e.target.value });
                        }}
                    />
                </Col>
            </Form.Group>
            <Form.Group as={Form.Row}>
                <Form.Label column sm={3}>Phone number</Form.Label>
                <Col sm={9}>
                    <Form.Control
                        name="phone"
                        type="phone"
                        isValid={customerDetails.phoneNumber}
                        placeholder="(000) 000-0000"
                        required
                        onChange={(e) => {
                            setBillingDetails({ ...billingDetails, phone: e.target.value });
                            setCustomerDetails({ ...customerDetails, phoneNumber: e.target.value });
                        }}
                    />

                </Col>
            </Form.Group>
            <Form.Group as={Form.Row}>
                <Form.Label column sm={3}>Email address</Form.Label>
                <Col sm={9}>
                    <Form.Control
                        name="email"
                        type="email"
                        placeholder="jane@doe.com"
                        isValid={customerDetails.email.includes("@")}
                        required
                        onChange={(e) => {
                            setBillingDetails({ ...billingDetails, email: e.target.value });
                            setCustomerDetails({ ...customerDetails, email: e.target.value });
                        }}
                    />
                </Col>
            </Form.Group>
            <Form.Group as={Form.Row}>
                <Form.Label column sm={3} >Mailing address</Form.Label>
                <Col sm={9}>
                    <Form.Control
                        name="address1"
                        type="text"
                        placeholder="Address"
                        required
                        isValid={customerDetails.mailingAddress1}
                        onChange={(e) => {
                            setCustomerDetails({ ...customerDetails, mailingAddress1: e.target.value });
                        }}
                    />
                </Col>

                <Form.Label column sm={3} />
                <Col sm={9}>
                    <Form.Control
                        name="address2"
                        type="text"
                        placeholder="Address, cont."
                        isValid={customerDetails.mailingAddress2}
                        onChange={(e) => {
                            setCustomerDetails({ ...customerDetails, mailingAddress2: e.target.value });
                        }}
                    />
                </Col>

                <Form.Label column sm={3} />
                <Col sm={5}>
                    <Form.Control
                        name="city"
                        type="text"
                        placeholder="City"
                        required
                        isValid={customerDetails.city}
                        onChange={(e) => {
                            setCustomerDetails({ ...customerDetails, city: e.target.value });
                        }}
                    />
                </Col>
                <Col sm={2}>
                    <Form.Control
                        name="state"
                        type="text"
                        placeholder="State"
                        required
                        isValid={customerDetails.state}
                        onChange={(e) => {
                            setCustomerDetails({ ...customerDetails, state: e.target.value });
                        }}
                    />
                </Col>
                <Col sm={2}>
                    <Form.Control
                        name="zip"
                        type="text"
                        placeholder="Zip"
                        required
                        isValid={customerDetails.zip}
                        onChange={(e) => {
                            setCustomerDetails({ ...customerDetails, zip: e.target.value });
                        }}
                    />
                </Col>
            </Form.Group>
            <br />
            <Form.Group as={Form.Row}>
                <Form.Label column sm={3} />
                <Col sm={9}>
                    <img src={require("static/cards/mastercard.png")} alt="Mastercard" height={50} width={61} />
                    <img src={require("static/cards/visa.jpg")} alt="Visa" height={30} width={60} />
                    <img src={require("static/cards/american_express.png")} alt="American Express" height={50} width={50} />
                    <img src={require("static/cards/discover.png")} alt="Discover" height={50} width={50} />
                </Col>
                <Form.Label column sm={3} />
                <Col sm={9}>
                    <Form.Text className="lightGrayText">
                        We'll never share your credit card information with anyone else.
                    </Form.Text>
                </Col>
                <Form.Label column sm={3}>Billing name</Form.Label>
                <Col sm={9}>
                    <Form.Control
                        name="billingName"
                        type="text"
                        placeholder="Jane S. Doe"
                        required
                        isValid={customerDetails.billingName}
                        onChange={(e) => {
                            setBillingDetails({ ...billingDetails, name: e.target.value });
                            setCustomerDetails({ ...customerDetails, billingName: e.target.value });
                        }}
                    />
                </Col>
                <Form.Label column sm={3}>Credit card info</Form.Label>
                <Col sm={9}>
                    <Card>
                        <CardElement
                            options={CARD_OPTIONS}
                            onChange={(e) => {
                                setCardComplete(e.complete);
                            }}
                        />
                    </Card>
                </Col>
            </Form.Group>
            <Form.Group as={Form.Row} controlId="formSubmit">
                <Form.Label column sm={10} />
                <Col sm={2}>
                    <Button
                        variant="blue"
                        type="submit"
                        disabled={showAreYouSure || !stripe || !elements || processing || !Number.isFinite(customerDetails.amountInDollars)}
                    >
                        {processing ? 'Processing...' : 'Submit'}
                    </Button>
                </Col>
            </Form.Group>
        </Form>
    );

    const areYouSureCard = <div className="areYouSure">
        <h3>Please confirm:</h3>
        <div>
            We are about to charge your card <b>${customerDetails.amountInDollars}</b>.
            A receipt will be emailed to you at <b>{customerDetails.email}</b> after confirmation.
        </div>
        <div>
            Are you sure you want to charge your card?
        </div>
        <div className="buttons">
            <Button 
                variant="blue" 
                onClick={submitCharge}
                disabled={processing}
            >
                {processing ? "Processing..." : "Authorize Payment"
                }
            </Button>
            <Button 
                variant="secondary"
                onClick={() => setShowAreYouSure(false)}
                disabled={processing}
            >
                Cancel
            </Button>
        </div>
    </div>

    const successfulCard = <div>
        We have successfully processed your payment. Please check your inbox at <b>{customerDetails.email}</b>
        &nbsp;for your receipt.
    </div>;

    const mainCard = <div>
        {form}
        {showAreYouSure ? areYouSureCard : null}
    </div>

    const errorCard = <div>
        There was an error processing your transaction. Your card has not been charged. Please contact the
        site owner to process your payment.
    </div>

    const notYetSuccessful = error ? errorCard : mainCard;

    return processedSuccessfully ? successfulCard : notYetSuccessful;
};

const PaymentPage = () => {
    return <div className='pay-container'>
        <Elements stripe={stripePromise} options={LATO_FONT}>
            <CheckoutForm />
        </Elements>
    </div>;
};

export default PaymentPage;



// WEBPACK FOOTER //
// ./src/components/PaymentPage/PaymentPage.js